import { FieldErrorsImpl } from 'react-hook-form'
import { CustomerModel } from 'pages/Customers/helpers/types'
import { RiskScore } from 'pages/EmbeddedCustomers/helpers/types'
import { WithPaginationApiResponseExtended } from './pagination'
import { PaginationQueryParams } from './reactQuery'
import { Currencies, Pageable, Sort, WalletTypes } from './types'

export const businessCustomerFields: AddCustomerPayloadKeys[] = [
  'customerEntityType',
  'businessName',
  'industry',
  'businessDescription',
  'registrationNumber',
  'businessMonthlyVolume',
  'businessTaxResidency',
  'countryCode',
  'addressLine1',
  'city',
  'postalCode',
  // Business representitive details
  'firstName',
  'lastName',
  'dateOfBirth',
  'nationality',
]

export const individualCustomerFields: AddCustomerPayloadKeys[] = [
  'customerEntityType',
  'firstName',
  'lastName',
  'dateOfBirth',
  'personalIdNumber',
  'countryCode',
  'addressLine1',
  'city',
  'postalCode',
  'nationality',
  'stateCode',
]

export const CustomerEntityType = {
  COMPANY: 'COMPANY',
  INDIVIDUAL: 'INDIVIDUAL',
} as const

export type CustomerEntityType = (typeof CustomerEntityType)[keyof typeof CustomerEntityType]

export const CustomerStatus = {
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
} as const

export type CustomerStatusType = (typeof CustomerStatus)[keyof typeof CustomerStatus]

export type CustomerStatusHighlightType = 'success' | 'warning' | 'error' | 'neutral'

export const CustomerDocumentStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  COMPLETE: 'COMPLETE',
  IN_PROGRESS: 'IN_PROGRESS',
  INT: 'INT',
  INFO_REQUIRED: 'INFO_REQUIRED',
  DECLINE: 'DECLINED',
  REJECTED: 'REJECTED',
  VERIFIED: 'VERIFIED',
}

export type CustomerDocumentStatusType =
  (typeof CustomerDocumentStatus)[keyof typeof CustomerDocumentStatus]

export const AccountType = {
  VIRTUAL_ACCOUNT: 'VIRTUAL_ACCOUNT',
  DEPOSIT_ACCOUNT: 'DEPOSIT_ACCOUNT',
} as const

export type AccountType = (typeof AccountType)[keyof typeof AccountType]

export interface CustomerShared<StatusType> {
  reference: string
  externalReference?: string
  status: StatusType
  type: CustomerEntityType
}

export type CustomerSummary = {
  name: string
  description: string
}

export type CustomerType = CustomerShared<CustomerStatusType>

export type Customer = CustomerType & CustomerSummary

export interface CustomerAddress {
  addressLine1: string
  addressLine2?: string
  city: string
  postalCode: string
  countryCode: string
  stateCode?: string
}
interface AddBusinessCustomerRequestDetails {
  name: string
  description: string
  taxResidenceCountryCode: string
  registrationNumber: string
  industryReference: string
  monthlyExpectedVolumesReference: string
  address: CustomerAddress
  representative: {
    firstName: string
    lastName: string
    dateOfBirth: string
    address: { countryCode: string }
    birthCountryCode?: string
  }
}

interface AddIndividualCustomerRequestDetails {
  firstName: string
  lastName: string
  dateOfBirth: string
  documentNumber: string
  // @deprecated
  birthCountryCode: string
  nationality?: string
  residenceCountryCode: string
  address: CustomerAddress
  description: string
}
export interface AddCustomerRequest {
  type: string
  externalReference: string | null
  company?: AddBusinessCustomerRequestDetails
  individual?: AddIndividualCustomerRequestDetails
}

export interface Address {
  addressLine1: string
  addressLine2?: string | null
  city: string
  postalCode: string
  countryCode: string
  stateCode?: string
  country: string
}

export interface MonthlyExpectedVolumes {
  reference: string
  name: string
  min: number
  max: number
}

export interface Industry {
  reference: string
  name: string
}

export interface CustomerPerson {
  reference?: string
  firstName: string
  lastName: string
  dateOfBirth: string
  address: Address
}

export interface Representative extends CustomerPerson {
  countryCode: string
}

export interface Business {
  name: string
  description: string
  taxResidenceCountryCode: string
  registrationNumber: string
  address: Address
  representative: Representative
  industry: Industry
  monthlyExpectedVolumes: MonthlyExpectedVolumes
}

export interface IndividualDetails {
  birthCountryCode: string
  documentNumber: string
}

export interface Individual {
  description?: string
  details: IndividualDetails
  person: {
    firstName: string
    lastName: string
    dateOfBirth: string
    address: Address
  }
}

export interface BusinessCustomerType extends CustomerType {
  company: Business
  reference: string
  riskScore?: RiskScore
}

export interface IndividualCustomerType extends CustomerType {
  individual: Individual
}

export type NestedCustomerDetails = BusinessCustomerType | IndividualCustomerType

export interface AddCustomerPayload {
  // General
  customerEntityType?: CustomerEntityType
  customerType?: string
  externalReference?: string
  countryCode?: string
  countryName?: string
  stateCode?: string
  stateName?: string
  addressLine1?: string
  addressLine2?: string | null
  city?: string
  postalCode?: string
  nationality?: string
  nationalityName?: string
  // Individual
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  email?: string
  phoneNumber?: string
  annualSalary?: string
  personalIdNumber?: string
  individualCustomerDescription?: string | null
  jobTitle: string
  sourceOfFunds: string
  // Business
  businessName?: string
  industry?: string
  industryName?: string
  businessDescription?: string
  registrationNumber?: string
  businessMonthlyVolume?: string
  businessMonthlyVolumeName?: string
  businessTaxResidency?: string
  businessTaxResidencyName?: string
  riskScore?: RiskScore
  eddCompleted?: boolean
}

export type AddCustomerPayloadKeys = keyof AddCustomerPayload

export type AddCustomerFormErrors = Partial<FieldErrorsImpl<AddCustomerPayload>>

export const LedgerWalletType = {
  FIAT: 'FIAT',
  CRYPTO: 'CRYPTO',
}

export type LedgerType = (typeof LedgerWalletType)[keyof typeof LedgerWalletType]

export interface MonthlyVolume {
  id: number
  max: number
  min: number
  name: string
  reference: string
}

export interface Industries {
  reference: string
  name: string
  children: Array<{
    reference: string
    name: string
  }>
}

export interface Instructions {
  type: WalletTypes // FIAT or CRYPTO
  virtualAccountRequired: boolean
  walletProfile: string
}

export interface AddCustomerWalletRequestV1 {
  currencyCode: Currencies
  name: string
  customerReference: string
  instruction: Instructions
}

export interface AddCustomerWalletRequestV2 {
  currency: Currencies
  name: string
  customerId: string
  profileId: string
}

export interface WalletBalance {
  value: number
  currencyCode: string
}

export interface WalletResponse {
  id: string
  accountReference: string
  customerReference: string
  name: string
  status: string
  balance: WalletBalance
  ledgers: any[]
}
export interface CustomerWalletsResponse {
  content: CustomerWallet[]
  pageable: Pageable
  totalPages: number
  totalElements: number
  last: boolean
  first: boolean
  sort: Sort
  numberOfElements: number
  size: number
  number: number
  empty: boolean
}

export interface CustomerWalletV2Balance {
  amount: number
  currency: string
}

export interface CustomerWalletV2Capabilities {
  safeguarded: boolean
}

export interface CustomerWalletV2 extends Omit<
  CustomerWallet,
  'accountReference' | 'customerReference' | 'balance' | 'capabilities'
> {
  customerId?: string
  customer?: { id: string }
  balance: CustomerWalletV2Balance
  capabilities: CustomerWalletV2Capabilities
}

export interface CustomerWalletV2Individual {
  id: string
  name: string
  customer: { id: string }
  status: CustomerWalletStatus
  balance: CustomerWalletV2Balance
  ledgers: Ledger[]
  paymentInstruments?: Ledger[]
  capabilities: CustomerWalletV2Capabilities & { currencyType?: string }
}

export interface CustomerWalletsV2Response {
  content: CustomerWalletV2[]
  pageable: { pageNumber: number; pageSize: number }
  hasNext: boolean
}

export const CustomerWalletStatusType = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  TERMINATED: 'TERMINATED',
} as const

export type CustomerWalletStatus =
  (typeof CustomerWalletStatusType)[keyof typeof CustomerWalletStatusType]

export const LedgerWalletCapabilities = {
  SAFEGUARDED: 'SAFEGUARDED',
} as const

export type LedgerWalletCapability =
  (typeof LedgerWalletCapabilities)[keyof typeof LedgerWalletCapabilities]

export interface CustomerWallet {
  id: string
  accountReference: string
  customerReference: string
  name: string
  status: CustomerWalletStatus
  balance: Balance
  ledgers: Ledger[]
  paymentInstruments?: Ledger[]
  capabilities: LedgerWalletCapability[]
}

export interface OrchestratedCustomerWallet extends CustomerWallet {
  supportsThirdParty: boolean
  supportsDeposits: boolean
  supportsWithdrawals: boolean
  supportsInternalTransfers: boolean
}

export interface CustomerWalletDetails extends CustomerWallet {
  legacyWalletId: number
}

export interface CustomerWalletDepositDetails {
  wallet: CustomerWallet
  customer: NestedCustomerDetails
}

export const AccountNumberTypes = {
  IBAN: 'IBAN',
  SCAN: 'SCAN',
  SWIFT: 'SWIFT',
} as const

export type AccountNumberType = (typeof AccountNumberTypes)[keyof typeof AccountNumberTypes]

export interface LedgerAddress {
  addressLine1?: string
  addressLine2?: string
  city?: string
  country?: string
  countryCode?: string
  postCode?: string
  region?: string
}

export interface LedgerBank {
  identificationCode?: string
  name?: string
  address?: LedgerAddress
}

export interface Ledger {
  status?: string
  type: LedgerType
  // FIAT
  accountNumberFormat?: AccountNumberType
  accountNumber?: string
  code?: string
  accountHolderName?: string
  paymentReference?: string
  paymentReferenceRequiredPrefix?: string
  bank?: LedgerBank
  beneficiaryAddress?: LedgerAddress
  // CRYPTO
  address?: string
  network?: string
  tag?: string
}

export interface PaymentInstrumentBankAddress {
  addressLine1?: string
  addressLine2?: string
  city?: string
  country?: string
  stateCode?: string
  postCode?: string
  fullAddress?: string
}

export interface PaymentInstrumentNid {
  type?: string
  value?: string
}

export interface PaymentInstrumentBankDetails {
  bic?: string
  name?: string
  nid?: PaymentInstrumentNid
  address?: PaymentInstrumentBankAddress
}

export interface PaymentInstrument {
  type: LedgerType
  accountHolderName?: string
  accountHolderAddress?: PaymentInstrumentBankAddress
  accountNumber?: string
  remittanceInformationPrefix?: string
  paymentReferenceRequiredPrefix?: string
  bankDetails?: PaymentInstrumentBankDetails
}

export interface LedgerV2WalletResponse {
  id: string
  name: string
  status: string
  balance: CustomerWalletV2Balance
  paymentInstruments: PaymentInstrument[]
  createdAt?: string
  updatedAt?: string
}

export interface Balance {
  value: number
  currencyCode: string
}

export type CustomersFilters = {
  models?: CustomerModel[]
  types?: CustomerEntityType[]
  statuses?: string[]
}

export interface CustomersRequest extends PaginationQueryParams {
  search?: string
  filters?: CustomersFilters
}

export interface CustomerWalletsRequest extends PaginationQueryParams {
  customerReference: string
  currencyCodeSearch?: string
  currencyCodes?: string[]
  currencyType?: LedgerType
  searchTerm?: string
  statuses?: CustomerWalletStatus[]
}

export interface GetCustomerWalletProfilesRequest {
  currencyCodes?: string[]
  paymentMethods?: string[]
  customerId?: string
}

export interface WalletProfile {
  id: string
  currencies: string[]
  methods: string[]
}

export interface CustomerWalletProfile {
  profiles: WalletProfile[]
}

export interface CustomerWalletProfileV2Response {
  content: WalletProfile[]
  totalPages: number
}

export interface AssignCustomerFeeWalletPayload {
  destinationWalletId: string
  currency: string
}

export interface AssignCustomerFeeWalletResponse {
  // add response
}

export interface GetCustomerFeeWalletsPayload {
  currency: string
}

export interface FeeWallet {
  destinationWalletId: string
  currency: string
}

export interface GetCustomerFeeWalletsResponse extends WithPaginationApiResponseExtended<
  FeeWallet[]
> {}

export interface SetCustomerFeeFormData {
  customerId: string
  payinsEnabled: boolean
  payoutsEnabled: boolean
  currency: string
  feeStructure: string
  fixedFee: string
  percentageFee: string
}

export interface ScrollToFieldOptions {
  position?: number
  element?: HTMLElement
}

export type ScrollToField = (options?: ScrollToFieldOptions) => void

// V2 Application types

export type CustomerV2Status =
  | 'INFO_REQUIRED'
  | 'PENDING'
  | 'ACTIONS_REQUIRED'
  | 'VERIFIED'
  | 'REJECTED'
  | 'TERMINATED'
  | 'AGREEMENTS_PENDING'

export interface AuthenticatedLink {
  link: string
  expiresAt: string
}

export interface Application {
  id: string
  reference: string
  status: CustomerV2Status
  type: string
  model: string
  name: string
  createdAt: string
  authenticatedLink: AuthenticatedLink
}

export interface BusinessCustomerResponse {
  id: string
  status: string
}

export interface BusinessProfileDetails {
  naicsCode: string
  monthlyExpectedVolumes: string
  isRegulated: boolean
  sourceOfFunds: string
  intendedUseOfAccount: string
  website: string
  regulatorInformation?: RegulatorInformation
}
export interface RegulatorInformation {
  regulatorName: string
  regulatorJurisdiction: string
}
export interface AddressDetails {
  addressLine1: string
  city: string
  postalCode: string
  countryCode: string
}
export interface TaxDetails {
  number: string
  taxResidenceCountryCode: string
}
export interface AssociateIssues {
  field: string
  message: string
}
export interface AssociateValidation {
  status: string
  issues: AssociateIssues[]
}
export interface AssociateIdentificationDocument {
  id: string
  externalId: string | null
  type: string
  subType: string | null
  deactivated: boolean
}
export interface AssociateProofOfAddress {
  documentType: string
}
export interface AssociateEntity {
  firstName: string
  lastName: string
  identificationDocuments: AssociateIdentificationDocument[]
  proofOfAddresses: AssociateProofOfAddress[]
  documents?: AssociateIdentificationDocument[]
}
export interface AssociateDetails {
  id: string
  validation: AssociateValidation
  types: string[]
  entity?: AssociateEntity
}
export interface Associates {
  displayName: string
  types: string[]
  id: string
  validation: AssociateValidation
  documents?: AssociateIdentificationDocument[]
}
export interface CompanyDetails {
  name: string
  entityType: string
  taxIdentification: TaxDetails
  registrationNumber: string
  address: AddressDetails
  isOperationalAddressDifferent: boolean
  businessProfile: BusinessProfileDetails
  operationalAddress?: AddressDetails
  incorporationDate: string
  businessOperationsStartDate: string
  associates?: AssociateDetails[]
}
export interface BusinessCustomerPayload {
  useCase?: string
  company: CompanyDetails
}
export interface CreateBusinessCustomer extends BusinessCustomerPayload {
  onboardingType: string
}

export interface ApplicationOnboarding {
  documentsIntakeEmail: string
}

export interface ApplicationUserLink {
  link: string
  linkType: string
  linkExpiresAt: string
}

export interface ApplicationUser {
  id: string
  email: string
  link: ApplicationUserLink
  createdAt: string
  updatedAt: string
}

export interface RequiredActions {
  type: string
  code: string
  category: string
  description: string
}

export interface IndividualCdd {
  employmentStatus: string | null
  expectedMonthlyVolume: string | null
  intendedUseOfAccount: string | null
  pepStatus: string | null
  sourceOfFunds: string | null
}

export interface IndividualApplicationDetails {
  firstName: string | null
  lastName: string | null
  emailAddress: string | null
  phoneNumber: string | null
  dateOfBirth: string | null
  nationality: string | null
  placeOfBirth: string | null
  documentNumber: string | null
  taxIdentification: {
    number: string | null
    taxResidenceCountryCode: string | null
  } | null
  address: {
    addressLine1: string | null
    addressLine2: string | null
    city: string | null
    postalCode: string | null
    stateCode: string | null
    countryCode: string | null
  } | null
  cdd: IndividualCdd | null
}

export interface CustomerApplicationDetails {
  id: string
  status: string
  onboardingType: string
  customerType: string
  onboardingApplication: ApplicationOnboarding
  users: ApplicationUser[]
  company?: CompanyDetails
  individual?: IndividualApplicationDetails
  authenticatedLink: AuthenticatedLink
  requiredActions: RequiredActions[]
}

export interface InfoRequiredData {
  code: string
  name: string
  category: string
}

export interface CustomerBlockers {
  code: string
  message: string
}
export interface CustomerApplicantStatus {
  data: InfoRequiredData[]
  missingRoles: string[]
  blockers: CustomerBlockers[]
}
export interface CustomerApplicationStatusDetails {
  infoRequired: CustomerApplicantStatus
}

export interface CustomerDocumentsV2 {
  status: string
  filename: string
  type: string | null
  id: string
}

export interface CustomerDocumentContent {
  downloadUrl: string
  expiresAt: string
  filename: string
}
export interface CustomerAgreements {
  id: string
  name: string
  description: string
  declinable: boolean
  status: string
}
export interface CustomerAgreementCreation {
  id: string
  reference: string
  agreements: CustomerAgreements[]
  signingUrl: string
}

export interface CreateCustomerAgreement {
  reference: string
  useCase: string
  customerType: string
  countryCode: string
  customerName: string
}
